/**
 * All-day calendar rows (time off, company holidays, project installs) are stored in
 * Firestore as `Timestamp`s, but they represent a *calendar day*, not a precise instant.
 * Different write paths anchor that instant differently:
 *   - The People Hub time-off forms store company-local (Central) midnight, e.g.
 *     `2026-06-15T05:00:00Z` (CDT) / `2026-06-15T06:00:00Z` (CST).
 *   - Cross-subsystem / legacy paths that do `new Date("2026-06-15")` store UTC midnight,
 *     i.e. `2026-06-15T00:00:00Z`.
 *
 * Reading either one with the viewer's *local* date (e.g. `startOfDay(ts.toDate())`) places
 * the UTC-midnight rows on the previous day for anyone west of UTC (every US zone), which is
 * the "team calendar is in the wrong time zone / off by one day" report.
 *
 * For a US (negative-offset) company the UTC calendar date of the stored instant equals the
 * intended day for BOTH conventions, and — unlike a local read — it does not depend on the
 * viewer's machine timezone. So we anchor all-day rows to their UTC calendar date.
 */
export function allDayCalendarDate(value: Date): Date {
  return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}

/** `YYYY-MM-DD` for an all-day marker, anchored to its UTC calendar date. */
export function allDayCalendarYmd(value: Date): string {
  const y = value.getUTCFullYear();
  const m = String(value.getUTCMonth() + 1).padStart(2, '0');
  const d = String(value.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
